import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, IconButton, InputAdornment, Popover, TextField, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTruck, faTruckFast, faTruckRampBox, faTruckMoving,
    faBox, faBoxesStacked, faBoxesPacking,
    faWarehouse, faStore, faGift, faTag, faTags, faPercent,
    faFire, faSnowflake, faLeaf, faStar, faHeart,
    faClock, faHourglass, faHourglassHalf, faCalendarDays,
    faBolt, faCircleExclamation, faCircleInfo, faCircleCheck, faTriangleExclamation,
    faSun, faMoon, faRecycle, faShield, faBell,
    faCartShopping, faBasketShopping, faSackDollar, faCoins, faMoneyBillWave,
    faShippingFast,
} from '@fortawesome/free-solid-svg-icons';

// Curated FA-solid icon set, picked for shop/stock/availability contexts.
// `name` is the dash-case Font Awesome identifier the storefront stores
// (so we can later swap to whatever rendering layer we want, e.g. CSS
// classes or react-fontawesome). `keywords` powers the search.
export const ICONS = [
    { name: 'truck',                icon: faTruck,                label: 'Truck',                 keywords: 'shipping deliver delivery' },
    { name: 'truck-fast',           icon: faTruckFast,            label: 'Truck (fast)',          keywords: 'express quick speed shipping' },
    { name: 'truck-ramp-box',       icon: faTruckRampBox,         label: 'Truck loading',         keywords: 'truck-loading external supplier dispatch' },
    { name: 'truck-moving',         icon: faTruckMoving,          label: 'Truck moving',          keywords: 'transport move' },
    { name: 'shipping-fast',        icon: faShippingFast,         label: 'Shipping fast',         keywords: 'express delivery' },
    { name: 'box',                  icon: faBox,                  label: 'Box',                   keywords: 'package shipping inventory' },
    { name: 'boxes-stacked',        icon: faBoxesStacked,         label: 'Boxes stacked',         keywords: 'packages inventory' },
    { name: 'boxes-packing',        icon: faBoxesPacking,         label: 'Boxes packing',         keywords: 'inventory pack' },
    { name: 'warehouse',            icon: faWarehouse,            label: 'Warehouse',             keywords: 'storage stock' },
    { name: 'store',                icon: faStore,                label: 'Store',                 keywords: 'shop' },
    { name: 'gift',                 icon: faGift,                 label: 'Gift',                  keywords: 'present voucher' },
    { name: 'tag',                  icon: faTag,                  label: 'Tag',                   keywords: 'label price discount' },
    { name: 'tags',                 icon: faTags,                 label: 'Tags',                  keywords: 'labels prices' },
    { name: 'percent',              icon: faPercent,              label: 'Percent',               keywords: 'discount sale' },
    { name: 'fire',                 icon: faFire,                 label: 'Fire',                  keywords: 'hot trending sale' },
    { name: 'snowflake',            icon: faSnowflake,            label: 'Snowflake',             keywords: 'winter cold seasonal' },
    { name: 'leaf',                 icon: faLeaf,                 label: 'Leaf',                  keywords: 'eco green sustainable' },
    { name: 'star',                 icon: faStar,                 label: 'Star',                  keywords: 'favorite featured rating' },
    { name: 'heart',                icon: faHeart,                label: 'Heart',                 keywords: 'favorite love wishlist' },
    { name: 'clock',                icon: faClock,                label: 'Clock',                 keywords: 'time wait delay' },
    { name: 'hourglass',            icon: faHourglass,            label: 'Hourglass',             keywords: 'wait time' },
    { name: 'hourglass-half',       icon: faHourglassHalf,        label: 'Hourglass half',        keywords: 'wait time' },
    { name: 'calendar-days',        icon: faCalendarDays,         label: 'Calendar',              keywords: 'date schedule' },
    { name: 'bolt',                 icon: faBolt,                 label: 'Bolt',                  keywords: 'fast lightning express' },
    { name: 'circle-exclamation',   icon: faCircleExclamation,    label: 'Alert',                 keywords: 'warn warning info notice' },
    { name: 'circle-info',          icon: faCircleInfo,           label: 'Info',                  keywords: 'information' },
    { name: 'circle-check',         icon: faCircleCheck,          label: 'Check',                 keywords: 'ok confirm available' },
    { name: 'triangle-exclamation', icon: faTriangleExclamation,  label: 'Warning',               keywords: 'warn alert caution' },
    { name: 'sun',                  icon: faSun,                  label: 'Sun',                   keywords: 'summer light day' },
    { name: 'moon',                 icon: faMoon,                 label: 'Moon',                  keywords: 'night dark' },
    { name: 'recycle',              icon: faRecycle,              label: 'Recycle',               keywords: 'eco return reusable' },
    { name: 'shield',               icon: faShield,               label: 'Shield',                keywords: 'protect security guarantee' },
    { name: 'bell',                 icon: faBell,                 label: 'Bell',                  keywords: 'notification alert' },
    { name: 'cart-shopping',        icon: faCartShopping,         label: 'Cart',                  keywords: 'shopping basket buy' },
    { name: 'basket-shopping',      icon: faBasketShopping,       label: 'Basket',                keywords: 'shopping cart buy' },
    { name: 'sack-dollar',          icon: faSackDollar,           label: 'Money sack',            keywords: 'price money' },
    { name: 'coins',                icon: faCoins,                label: 'Coins',                 keywords: 'money price currency' },
    { name: 'money-bill-wave',      icon: faMoneyBillWave,        label: 'Money bill',            keywords: 'cash currency' },
];

// Look up an icon by its FA dash-case name. Falls back to faTruckRampBox
// if the stored value points at something we no longer ship.
export const findIcon = (name) => ICONS.find(i => i.name === name) || ICONS.find(i => i.name === 'truck-ramp-box');

const IconPicker = ({ value, color = '#000', onChange, label, placeholderLabel = 'Pick icon' }) => {
    const [anchor, setAnchor]   = useState(null);
    const [search, setSearch]   = useState('');
    const open    = Boolean(anchor);
    const current = findIcon(value);

    const matches = ICONS.filter(i => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return i.name.includes(q) || i.label.toLowerCase().includes(q) || i.keywords.toLowerCase().includes(q);
    });

    return (
        <>
            <ButtonBase
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    p: 1, borderRadius: 1.5,
                    border: '1px solid', borderColor: 'divider',
                    backgroundColor: '#fff',
                    minWidth: 200,
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: 'primary.main' },
                }}
            >
                <Box sx={{
                    width: 36, height: 36, borderRadius: 1,
                    backgroundColor: 'rgba(0,0,0,0.04)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color,
                    flexShrink: 0,
                }}>
                    {current
                        ? <FontAwesomeIcon icon={current.icon} style={{ fontSize: 18 }} />
                        : <Box sx={{ color: 'text.disabled', fontSize: '0.7rem', fontStyle: 'italic' }}>?</Box>}
                </Box>
                <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                        {label || placeholderLabel}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {current ? current.label : placeholderLabel}
                    </Typography>
                    {current && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontFamily: 'monospace', fontSize: '0.65rem' }}>
                            fa-{current.name}
                        </Typography>
                    )}
                </Box>
            </ButtonBase>

            <Popover
                open={open}
                anchorEl={anchor}
                onClose={() => { setAnchor(null); setSearch(''); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { width: 360, maxHeight: 440, p: 1.5 } } }}
            >
                <TextField
                    autoFocus
                    size="small" fullWidth
                    placeholder="Search icons…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    sx={{ mb: 1.25 }}
                />
                {matches.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="caption">No icons match "{search}"</Typography>
                    </Box>
                ) : (
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(6, 1fr)',
                        gap: 0.5,
                        maxHeight: 320,
                        overflowY: 'auto',
                    }}>
                        {matches.map(i => {
                            const isSel = value === i.name;
                            return (
                                <Tooltip key={i.name} title={i.label} placement="top" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => { onChange(i.name); setAnchor(null); setSearch(''); }}
                                        sx={{
                                            width: 48, height: 48,
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: isSel ? 'primary.main' : 'transparent',
                                            backgroundColor: isSel ? 'rgba(25,118,210,0.08)' : 'transparent',
                                            color: isSel ? 'primary.main' : 'text.primary',
                                            '&:hover': { backgroundColor: 'rgba(25,118,210,0.06)' },
                                        }}
                                    >
                                        <FontAwesomeIcon icon={i.icon} style={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            );
                        })}
                    </Box>
                )}
            </Popover>
        </>
    );
};
IconPicker.propTypes = {
    value:            PropTypes.string,
    color:            PropTypes.string,
    onChange:         PropTypes.func,
    label:            PropTypes.string,
    placeholderLabel: PropTypes.string,
};

export default IconPicker;
